import { useMemo, useState } from 'react';
import { useRigg } from '@/data/RiggProvider.tsx';
import { api } from '@/lib/api.ts';
import { ago, initial } from '@/lib/format.ts';
import { Page, Row } from './Page.tsx';
import { Button, Dialog, Field, Input, Select, Tip, useToast } from '@/components/ui.tsx';

/**
 * People and approval levels.
 *
 * A member can sign a task off — "looks right to me" — which parks it in the
 * admin's queue. Only an admin approves the merge. The distinction exists
 * because a routine cannot verify who spoke to it, so *somebody* has to be the
 * one whose approval means something.
 */
export function PeopleScreen() {
  const { data, project, isAdmin, me } = useRigg();
  const { notify } = useToast();
  const [inviting, setInviting] = useState(false);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<'admin' | 'member'>('member');
  const [busy, setBusy] = useState(false);

  const members = useMemo(
    () =>
      Object.values(data.members)
        .map((member) => ({ member, profile: data.profiles[member.user_id] }))
        .filter((entry) => entry.profile)
        .sort((a, b) => (a.profile!.name ?? '').localeCompare(b.profile!.name ?? '')),
    [data.members, data.profiles],
  );

  const invites = useMemo(
    () => Object.values(data.invites).filter((invite) => !invite.accepted_at),
    [data.invites],
  );

  const invite = async () => {
    if (!project || !email.trim()) return;
    setBusy(true);
    try {
      const result = await api.invite(project.id, email.trim(), role);
      notify(
        result.joined ? 'Added to the project' : 'Invitation saved',
        {
          body: result.joined
            ? undefined
            : 'They join automatically the first time they sign in with that address.',
        },
      );
      setInviting(false);
      setEmail('');
    } catch (error) {
      notify('Could not invite them', { body: (error as Error).message, tone: 'bad' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Page
      title="People"
      blurb={
        <>
          Everyone here can see the board and sign work off. Only an <strong>admin</strong> can
          approve a merge, mark work merged or deployed, or manage the project.
        </>
      }
      actions={
        isAdmin ? (
          <Button tone="primary" onClick={() => setInviting(true)}>
            Invite someone
          </Button>
        ) : null
      }
    >
      <div>
        {members.map(({ member, profile }) => (
          <Row
            key={member.user_id}
            title={
              <span className="flex items-center gap-2.5">
                <span className="grid size-6 shrink-0 place-items-center rounded-full bg-avatar text-[11px] font-[650] text-t6">
                  {initial(profile!.name)}
                </span>
                {profile!.name}
                {member.user_id === me?.id ? (
                  <span className="text-[12px] font-normal text-mute">you</span>
                ) : null}
              </span>
            }
            subtitle={profile!.email ?? undefined}
            actions={
              isAdmin ? (
                <>
                  <Select
                    size="sm"
                    value={member.role}
                    onChange={async (next) => {
                      try {
                        await api.setRole(member.project_id, member.user_id, next as 'admin' | 'member');
                      } catch (error) {
                        notify('Could not change the role', {
                          body: (error as Error).message,
                          tone: 'bad',
                        });
                      }
                    }}
                    options={[
                      { value: 'member', label: 'Member' },
                      { value: 'admin', label: 'Admin' },
                    ]}
                    aria-label={`Role for ${profile!.name}`}
                  />
                  {member.user_id === me?.id ? null : (
                    <Button
                      size="sm"
                      tone="danger"
                      onClick={async () => {
                        try {
                          await api.removeMember(member.project_id, member.user_id);
                        } catch (error) {
                          notify('Could not remove them', {
                            body: (error as Error).message,
                            tone: 'bad',
                          });
                        }
                      }}
                    >
                      Remove
                    </Button>
                  )}
                </>
              ) : (
                <span className="text-[12.5px] font-semibold text-mute capitalize">
                  {member.role}
                </span>
              )
            }
          />
        ))}
      </div>

      {invites.length > 0 ? (
        <>
          <h2 className="mt-8 mb-1 text-[14px] font-[650] text-t1">Invited</h2>
          <p className="mb-2 text-[12.5px] text-mute">
            They become members the first time they sign in with that address.
          </p>
          <div>
            {invites.map((entry) => (
              <Row
                key={entry.id}
                title={entry.email}
                subtitle={`invited ${ago(entry.created_at)} · ${entry.role}`}
                actions={
                  isAdmin ? (
                    <Button
                      size="sm"
                      tone="danger"
                      onClick={() => void api.cancelInvite(entry.id).catch(() => undefined)}
                    >
                      Cancel
                    </Button>
                  ) : null
                }
              />
            ))}
          </div>
        </>
      ) : null}

      <Dialog
        open={inviting}
        onOpenChange={setInviting}
        title="Invite someone"
        description="They get a sign-in link by email and land straight in this project — there is no account to create."
        footer={
          <>
            <Button onClick={() => setInviting(false)}>Cancel</Button>
            <Button tone="primary" disabled={busy || !email.trim()} onClick={() => void invite()}>
              Invite
            </Button>
          </>
        }
      >
        <Field label="Email">
          <Input
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            placeholder="mel@example.com"
            autoFocus
          />
        </Field>
        <Field
          label="Role"
          hint="Admins approve merges and manage the project. Members review and sign off."
        >
          <Tip content="You can change this later.">
            <span>
              <Select
                value={role}
                onChange={(next) => setRole(next as 'admin' | 'member')}
                options={[
                  { value: 'member', label: 'Member' },
                  { value: 'admin', label: 'Admin' },
                ]}
              />
            </span>
          </Tip>
        </Field>
      </Dialog>
    </Page>
  );
}
